import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import {
  GlobeAltIcon,
  MapPinIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import productAnalyticsService from '../../services/api/product-analytics.service';

// Country code to flag emoji
const countryFlag = (code: string): string => {
  if (!code || code.length !== 2) return '';
  const offset = 127397;
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => c.charCodeAt(0) + offset));
};

interface CountryData {
  country: string;
  sessions: number;
  uniqueUsers: number;
}

interface CityData {
  city: string;
  country: string;
  sessions: number;
  uniqueUsers: number;
  latitude: number | null;
  longitude: number | null;
}

export default function GeographyPage() {
  const { t } = useTranslation('common');
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 30 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '30d',
  });
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [cities, setCities] = useState<CityData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [countriesData, citiesData] = await Promise.all([
          productAnalyticsService.getSessionsByCountry(
            selectedAppId || 'all',
            new Date(timeRange.startTime).toISOString(),
            new Date(timeRange.endTime).toISOString()
          ),
          productAnalyticsService.getSessionsByCity(
            selectedAppId || 'all',
            new Date(timeRange.startTime).toISOString(),
            new Date(timeRange.endTime).toISOString()
          ),
        ]);
        setCountries(countriesData || []);
        setCities(citiesData || []);
      } catch {
        setCountries([]);
        setCities([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedAppId, timeRange]);

  const totalSessions = countries.reduce((sum, c) => sum + c.sessions, 0);
  const topCountry = countries[0];

  // Cities with valid coordinates for the map
  const mappableCities = cities.filter(c => c.latitude != null && c.longitude != null);
  const maxCitySessions = mappableCities[0]?.sessions || 1;
  const getRadius = (sessions: number) => 6 + (sessions / maxCitySessions) * 29;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center space-x-3">
            <GlobeAltIcon className="w-8 h-8 text-cyan-600" />
            <span>{t('geography.title')}</span>
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('geography.subtitle')}
          </p>
        </div>

        {/* Time Range */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-cyan-500 rounded-lg">
                <GlobeAltIcon className="w-5 h-5 text-white" />
              </div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('geography.totalCountries')}</p>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{countries.length}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-purple-500 rounded-lg">
                <MapPinIcon className="w-5 h-5 text-white" />
              </div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('geography.topCountry')}</p>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {topCountry ? `${countryFlag(topCountry.country)} ${topCountry.country}` : 'N/A'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-green-500 rounded-lg">
                <UsersIcon className="w-5 h-5 text-white" />
              </div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('geography.sessionsWithGeo')}</p>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalSessions.toLocaleString()}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <>
            {/* Interactive Map - Cities */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('geography.sessionsByCity')}</h2>
              {mappableCities.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <GlobeAltIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>{t('geography.noDataTitle')}. {t('geography.noDataDescription')}</p>
                </div>
              ) : (
                <div className="rounded-xl overflow-hidden relative" style={{ height: 480, zIndex: 0 }}>
                  <MapContainer
                    center={[20, 0]}
                    zoom={2}
                    minZoom={2}
                    maxZoom={12}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {mappableCities.map((c) => (
                      <CircleMarker
                        key={`${c.city}-${c.country}`}
                        center={[c.latitude!, c.longitude!]}
                        radius={getRadius(c.sessions)}
                        pathOptions={{
                          fillColor: '#0891b2',
                          fillOpacity: 0.6,
                          color: '#0e7490',
                          weight: 2,
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -10]}>
                          <div className="text-sm">
                            <strong>{countryFlag(c.country)} {c.city}, {c.country}</strong>
                            <br />
                            {t('geography.sessions')}: {c.sessions.toLocaleString()}
                            <br />
                            {t('geography.users')}: {c.uniqueUsers.toLocaleString()}
                          </div>
                        </Tooltip>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                </div>
              )}
            </div>

            {/* Tables Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Countries Table */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('geography.topCountries')}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.country')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.sessions')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.users')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {countries.slice(0, 20).map((row, idx) => {
                        const pct = totalSessions > 0 ? ((row.sessions / totalSessions) * 100).toFixed(1) : '0';
                        return (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              <span className="mr-2">{countryFlag(row.country)}</span>
                              {row.country}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                              {row.sessions.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-300">
                              {row.uniqueUsers.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div className="h-2 rounded-full bg-cyan-500" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-gray-600 dark:text-gray-300 min-w-[40px]">{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {countries.length === 0 && (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">{t('geography.noData')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Cities Table */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('geography.topCities')}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.city')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.country')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.sessions')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('geography.users')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {cities.slice(0, 20).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{row.city}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            <span className="mr-1">{countryFlag(row.country)}</span>
                            {row.country}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                            {row.sessions.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-300">
                            {row.uniqueUsers.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {cities.length === 0 && (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">{t('geography.noData')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
